import { useState, useRef } from 'react';
import { effectFilters, rarityGlows } from '../constants/effects';
import { FISH_SPRITE_HEIGHTS, DEFAULT_CATCH_SPRITE_HEIGHT } from '../constants/assets';
import StarRating from './StarRating';
import { coinIcon } from '../constants/assets';

export default function FishDetailOverlay({ fish, onKeep, onSell, onSwipeLeft, onSwipeRight }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const touchStartX = useRef(null);
  const SWIPE_THRESHOLD = 50;

  if (!fish) return null;

  const spriteHeight = FISH_SPRITE_HEIGHTS[fish.id] ?? DEFAULT_CATCH_SPRITE_HEIGHT;

  const handleConfirmSell = () => {
    setShowConfirm(false);
    onSell(fish);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    if (showConfirm) {
      touchStartX.current = null;
      return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
    if (deltaX < 0) {
      onSwipeLeft?.();
    } else {
      onSwipeRight?.();
    }
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'absolute', inset: 0, backgroundColor: '#05070a',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: '20px',
      }}
    >

      {/* Swipe / click indicators */}
      <div style={{
        position: 'absolute', top: '35%', left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between',
        padding: '0 12px', transform: 'translateY(-50%)',
      }}>
        <button
          onClick={() => { if (!showConfirm) onSwipeRight?.(); }}
          disabled={!onSwipeRight}
          style={{
            background: 'none', border: 'none', cursor: onSwipeRight ? 'pointer' : 'default',
            color: onSwipeRight ? '#8b949e' : '#30363d', fontSize: '32px',
            padding: '10px', lineHeight: 1, fontFamily: 'inherit',
            opacity: onSwipeRight ? 1 : 0,
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={e => { if (onSwipeRight) e.target.style.color = '#ffffff'; }}
          onMouseLeave={e => { if (onSwipeRight) e.target.style.color = '#8b949e'; }}
        >
          ‹
        </button>
        <button
          onClick={() => { if (!showConfirm) onSwipeLeft?.(); }}
          disabled={!onSwipeLeft}
          style={{
            background: 'none', border: 'none', cursor: onSwipeLeft ? 'pointer' : 'default',
            color: onSwipeLeft ? '#8b949e' : '#30363d', fontSize: '32px',
            padding: '10px', lineHeight: 1, fontFamily: 'inherit',
            opacity: onSwipeLeft ? 1 : 0,
            transition: 'color 0.15s ease',
          }}
          onMouseEnter={e => { if (onSwipeLeft) e.target.style.color = '#ffffff'; }}
          onMouseLeave={e => { if (onSwipeLeft) e.target.style.color = '#8b949e'; }}
        >
          ›
        </button>
      </div>

      {/* Fish Sprite */}
      <div
        key={fish.instanceId ?? fish.caughtAt ?? fish.id}
        style={{
          filter: `drop-shadow(0 0 20px ${rarityGlows[fish.rarity.toLowerCase()] || '#ffffff'})`,
          padding: '30px',
          display: 'flex',
          justifyContent: 'center',
      }}>
        <img
          src={`assets/fish/${fish.id}.png`}
          alt={fish.name}
          className={fish.effect === 'prismatic' ? 'effect-prismatic' : ''}
          style={{
            width: 'auto',
            height: `${spriteHeight}px`,
            objectFit: 'contain',
            imageRendering: 'pixelated',
            filter: effectFilters[fish.effect.toLowerCase()] || 'none',
          }}
          onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=FISH'; }}
        />
      </div>

      {/* Text Info */}
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        {fish.effect !== 'default' && (
          <span
            className={`text-effect-${fish.effect.toLowerCase()}`}
            style={{ fontSize: '14px', display: 'block', color: '#ffffff' }}
          >
            {fish.effect}
          </span>
        )}
        <h2 style={{
          color: rarityGlows[fish.rarity.toLowerCase()] || 'white',
          fontSize: '28px', margin: '5px 0',
          textTransform: 'uppercase', textShadow: '0 2px 4px rgba(0,0,0,0.5)',
        }}>
          {fish.rarity} {fish.name}
        </h2>
      </div>

      {/* Stats */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '20px', backgroundColor: '#161b22',
        padding: '15px 25px', borderRadius: '8px',
        border: '1px solid #30363d', marginTop: '20px',
      }}>
        <StarRating count={fish.stars} size={20} />
        <span style={{ color: '#8b949e', fontSize: '15px' }}>{fish.weight} lbs</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#44ff44', fontSize: '18px' }}>
          <img src={coinIcon} alt="gold" style={{ width: '20px', height: '20px', imageRendering: 'pixelated' }} />
          {fish.sellPrice}
        </span>
      </div>

      {/* Position indicator */}
      <div style={{ marginTop: '12px', color: '#30363d', fontSize: '8px', letterSpacing: '1px' }}>
        {fish._index !== undefined ? `${fish._index + 1} / ${fish._total}` : ''}
      </div>

      {/* Actions */}
      <div style={{ marginTop: '18px', width: '100%', maxWidth: '300px' }}>
        {!showConfirm ? (
          <div style={{ display: 'flex', gap: '15px' }}>
            <button
              onClick={onKeep}
              style={{ flex: 1, padding: '15px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
            >
              KEEP
            </button>
            <button
              onClick={() => setShowConfirm(true)}
              style={{ flex: 1, padding: '15px', background: 'transparent', color: '#f1c40f', border: '2px solid #f1c40f', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
            >
              SELL
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#ff4d4d', fontSize: '11px', letterSpacing: '1px' }}>
              ARE YOU SURE?
            </span>
            <div style={{ display: 'flex', gap: '15px', width: '100%' }}>
              <button
                onClick={handleConfirmSell}
                style={{ flex: 1, padding: '15px', background: '#ff4d4d', color: '#fff', border: '2px solid #ff4d4d', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
              >
                YES
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                style={{ flex: 1, padding: '15px', background: 'transparent', color: '#8b949e', border: '2px solid #8b949e', borderRadius: '6px', fontFamily: 'inherit', cursor: 'pointer', fontSize: '12px' }}
              >
                NO
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}